import { ViewController } from '@estee/elc-service';
import { emit, events, on } from '@estee/elc-service-bus';
import { action, computed, observable } from 'mobx';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '~setup/CommunicationsService';
import { ProductNotifyMeRepository } from '../repositories/ProductNotifyMeRepository';
import { LoadProductUseCase } from '../use-cases/LoadProductUseCase';
import { ClearNotifyMeFormUseCase } from '../use-cases/ClearNotifyMeFormUseCase';
import {
    IProductNotifyMePopUp,
    IProductNotifyMePopUpConfig
} from '../../exported/product-notify-me/ProductNotifyMePopUp';
import { IWaitlist } from '../views/product-notify-me/ProductNotifyMePopUpContent';
import { EmailSubmittedUseCase } from '../use-cases/EmailSubmittedUseCase';

export class ProductNotifyMeViewController extends ViewController {
    private productNotifyMeRepository: ProductNotifyMeRepository;
    @observable private isProductNotifyMeConfirmationOpen: boolean = false;
    @observable private isPopUpOpen: boolean = false;
    @observable private acceptsTerms = false;
    @observable private isGdprPopupOpen: boolean = false;
    @observable private gdprConfirmation: boolean = false;
    @observable private emailEntered: boolean = false;
    private waitlist: IWaitlist;
    private skuId: string;
    private skuBaseId: string;
    private subscriptionType: string;

    constructor() {
        super();
        this.productNotifyMeRepository = diContainer.get(serviceNames.productNotifyMeRepository);
    }

    @on(events.PRODUCT_NOTIFY_ME_REQUEST_SUCCESS)
    public onProductNotifyMeSuccess = (data: {skuId: string}) => {
        if (this.skuId === data.skuId) {
            new EmailSubmittedUseCase().execute();
            this.onNotifyMeSuccess();
        }
    };

    @action
    public initialize(props: IProductNotifyMePopUpConfig) {
        this.waitlist = props.waitlist ?? {};
        this.skuId = props.skuId;
        this.skuBaseId = props.skuBaseId;
        this.subscriptionType = props.subscriptionType;
    }

    @computed
    public get data() {
        return {
            notifyMeForm: this.productNotifyMeRepository.getNotifyMeForm(),
            isProductNotifyMeConfirmationOpen: this.isProductNotifyMeConfirmationOpen,
            isPopUpOpen: this.isPopUpOpen,
            acceptsTerms: this.acceptsTerms,
            isGdprPopupOpen: this.isGdprPopupOpen,
            gdprConfirmation: this.gdprConfirmation,
            emailEntered: this.emailEntered
        };
    }

    @action
    public loaded = async (props: IProductNotifyMePopUp) => {
        this.isLoading = true;
        const loadProductUseCase = new LoadProductUseCase(props.config);
        loadProductUseCase.execute();
        this.isLoading = false;
    };

    @action
    public setAcceptsTerms = (value: boolean) => {
        this.acceptsTerms = value;
        this.gdprConfirmation = !this.acceptsTerms;
    };

    @action
    public onSubmitButtonClick = (gdpr: boolean) => {
        if (gdpr) {
            this.gdprConfirmation = !this.acceptsTerms;

            if (this.acceptsTerms) {
                this.getNotifyMeForm();
            }
        } else {
            this.getNotifyMeForm();
        }
    };

    private getNotifyMeForm = () => {
        const form = this.productNotifyMeRepository.getNotifyMeForm();

        emit(events.PRODUCT_NOTIFY_ME_REQUEST, {
            skuId: this.skuId,
            skuBaseId: this.skuBaseId,
            email: form.userEmail,
            subscriptionType: this.subscriptionType
        });
    };

    @action
    public onNotifyMeSuccess = () => {
        this.isProductNotifyMeConfirmationOpen = true;
    };

    @action
    public onCloseButtonClick = () => {
        this.isProductNotifyMeConfirmationOpen = false;
        this.isPopUpOpen = false;
    };

    @action
    public onNotifyMeClick = () => {
        if (this.waitlist.enableHybridModal) {
            this.onNotifyMeTriggered();
        } else {
            emit(events.PRODUCT_NOTIFY_ME_TRIGGERED, {
                skuId: this.skuId,
                skuBaseId: this.skuBaseId
            });
        }
    };

    @action
    private onNotifyMeTriggered = () => {
        const clearNotifyMeFormUseCase = new ClearNotifyMeFormUseCase();
        clearNotifyMeFormUseCase.execute();
        this.isProductNotifyMeConfirmationOpen = false;
        this.isPopUpOpen = true;
    };

    @action
    public onCloseNotifyMePopUp = () => {
        this.isPopUpOpen = false;
        this.isProductNotifyMeConfirmationOpen = false;
    };

    @action
    public toggleGdprPopupOpen = () => {
        this.isGdprPopupOpen = !this.isGdprPopupOpen;
    };
}
